import { graphql, navigate } from 'gatsby';
import React, { useEffect, useRef, useState } from 'react';
import { blindPostRequest } from '../../utils/network';
import PostHogHelper from '../../utils/posthog-helper';
import { getPropValues } from '../../utils/helpers';
import Consent from '../fragments/consent';
import FormError from '../fragments/formError';
import Honey, { honeyGenerator } from '../fragments/honey';

interface NewsletterProps {
  block: {
    _template: string;
    title: string;
    support_text: string;
    submit_label: string;
    email_placeholder: string;
    terms_text: string;
    subscribe_url: string;
    thank_you_url: string;
    images: Image[];
  };
}

type Image = {
  image: string;
  image_alt_text: string;
};

export const newsletterQuery = graphql`
  fragment NewsletterFragment on Block {
    title
    support_text
    submit_label
    email_placeholder
    terms_text
    subscribe_url
    thank_you_url
    images {
      image
      image_alt_text
    }
  }
`;

const Newsletter = ({ block }: NewsletterProps) => {
  const validRef = useRef<HTMLInputElement>(null);
  const consentRef = useRef<HTMLInputElement>(null);
  const subscribeUrl = getPropValues(
    block.subscribe_url,
    'https://api.bunny.net/newsletter/add-contact'
  );
  const thankYouPageUrl = getPropValues(block.thank_you_url, '/thank-you/');
  const submitLabel = getPropValues(block.submit_label, 'Subscribe');
  const emailPlaceholder = getPropValues(
    block.email_placeholder,
    'Email Address*'
  );
  const termsText = getPropValues(
    block.terms_text,
    'I consent to the processing of my email as decribed in <a href="/privacy">Privacy Policy</a>.'
  );

  const [formId, setFormId] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(true);
  const emailId = `ContactEmail${formId}`;
  const [imageLeft, imageRight] = block.images || [null, null];

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitted) return;

    setSubmitted(true);

    const formJson = {
      ContactEmail: email,
      IsValid: validRef.current?.checked ? 'true' : 'false',
      AgreeWithTerms: consentRef.current?.checked,
      Message: `I would like to join bunny.net's newsletter to stay in touch with our new case studies.`,
    };

    const postedSuccessfully = await blindPostRequest(subscribeUrl, formJson);

    if (postedSuccessfully) {
      await PostHogHelper.promiseToCaptureEmail(email);
      navigate(thankYouPageUrl);
    } else {
      setSuccess(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    setEmail(target.value);
  };

  useEffect(() => {
    setFormId(honeyGenerator());
  }, []);

  // Note: This could use form builder

  return (
    <section className={`block ${block._template}`}>
      {imageLeft && (
        <div className='newsletter-image__left'>
          <img
            src={imageLeft.image}
            alt={imageLeft.image_alt_text}
            title={imageLeft.image_alt_text}
          />
        </div>
      )}
      <div className='newsletter__container container'>
        <div className='row'>
          <h2>{block.title}</h2>
          <p className='form-support-text'>{block.support_text}</p>
        </div>
        <form
          className={`newsletter__form ${success ? 'visibility-hidden' : ''}`}
          onSubmit={handleSubmit}
        >
          <FormError
            submitted={submitted}
            success={success}
            className='newsletter__error-message'
          />
          <div className='newsletter__fields'>
            <Honey validRef={validRef} />
            <input
              type='email'
              id={emailId}
              name={emailId}
              className='newsletter__field'
              placeholder={emailPlaceholder}
              value={email}
              onChange={handleChange}
              required
            />
            <button
              type='submit'
              name='submit'
              className='cta cta-primary newsletter__cta cursor-pointer'
            >
              {submitLabel}
            </button>
          </div>
          <Consent
            consentRef={consentRef}
            formStyle='newsletter__terms'
            checkboxStyle='orange'
            text={termsText}
          />
        </form>
      </div>
      {imageRight && (
        <div className='newsletter-image__right'>
          <img
            src={imageRight.image}
            alt={imageRight.image_alt_text}
            title={imageRight.image_alt_text}
          />
        </div>
      )}
    </section>
  );
};

export default Newsletter;
